import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, RefreshCw, ShieldCheck } from 'lucide-react';
import { getTrustScore, getTrustScores, recomputeDiscoveredAgentTrustScore } from '../../../services/api';
import { publishedBandStyle } from '../../../services/trustBands';
import {
  Dial, FactorRow, DeductionRow, SectionLabel, Pill,
  PANEL_STYLE, CONTROL_STYLE, factorName, evidenceLabel,
} from './trustScoreParts';

/**
 * TrustScorePanel — a starting trust score for a subject with no runtime evidence yet.
 *
 * Every colour here comes from publishedBandStyle(), so the dial, the factor bars and the
 * deduction list are all resolved from one band rather than being tinted by hand. That is what
 * keeps this panel and the published scorecard showing the same thing for the same score.
 *
 * The score is a whole number, rounded down, and the band beside it is the band that number
 * resolves to — never the band the unrounded value would have reached.
 *
 * @param {string} props.discoveredAgentId  subject to score
 * @param {string} [props.scoreId]       existing passport to load, when one is known
 */
export function TrustScorePanel({ discoveredAgentId, scoreId, canRecompute = true }) {
  const [passport, setPassport] = useState(null);
  const [state, setState] = useState('idle');
  const [message, setMessage] = useState(null);

  const load = useCallback(async () => {
    setState('loading');

    // Resolve by id when the caller knows one, otherwise find this subject's passport in the
    // tenant's list. Without the second path a subject that HAS been scored reads as unscored
    // until someone presses the button, which is the same claim the empty state makes about an
    // agent nobody has looked at — two different situations saying the same thing.
    let id = scoreId;
    if (!id && discoveredAgentId) {
      const listRes = await getTrustScores();
      if (listRes?.ok === false) { setState('error'); return; }
      const rows = listRes?.data || listRes || [];
      const match = Array.isArray(rows)
        ? rows.find((row) => row.subjectRef === discoveredAgentId)
        : null;
      if (!match) { setState('none'); return; }
      id = match.scoreId;
    }
    if (!id) { setState('none'); return; }

    const res = await getTrustScore(id);
    if (res?.ok === false) { setState('error'); return; }
    setPassport(res?.data || res);
    setState('ready');
  }, [scoreId, discoveredAgentId]);

  useEffect(() => { load(); }, [load]);

  const recompute = async () => {
    if (!discoveredAgentId) return;
    setState('loading');
    setMessage(null);
    const res = await recomputeDiscoveredAgentTrustScore(discoveredAgentId);
    if (res?.ok === false) { setState('error'); return; }
    const body = res?.data || res;
    // The service answers with a guiding message, not a passport, when there is too little to
    // score. That is a normal outcome and reads as one.
    if (body && body.message && body.score === undefined) {
      setMessage(body.message);
      setState('none');
      return;
    }
    setPassport(body);
    setState('ready');
  };

  const style = useMemo(
    () => publishedBandStyle(passport?.score),
    [passport?.score],
  );

  const deductions = Array.isArray(passport?.deductions) ? passport.deductions.slice(0, 3) : [];

  return (
    <div style={PANEL_STYLE}>

      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
        <ShieldCheck size={14} style={{ color: 'var(--v2-text-muted)' }} />
        <span style={{ fontSize: 'var(--v2-trust-title-size)',
          fontWeight: 'var(--v2-trust-title-weight)' }}>Trust Score</span>
        <span style={{ fontSize: 'var(--v2-trust-meta-size)', color: 'var(--v2-text-muted)' }}>
          a starting score, before this agent has been measured
        </span>
        {canRecompute && (
          <button type="button" onClick={recompute} disabled={state === 'loading'}
            style={CONTROL_STYLE}>
            {state === 'loading'
              ? <Loader2 size={12} className="animate-spin" />
              : <RefreshCw size={12} />}
            {passport ? 'Rescore' : 'Score this agent'}
          </button>
        )}
      </div>

      {state === 'error' && (
        <div style={{ fontSize: 'var(--v2-trust-body-size)', color: 'var(--v2-danger)' }}>
          Couldn&rsquo;t load the trust score. Try again in a moment.
        </div>
      )}

      {state === 'none' && (
        <div style={{ fontSize: 'var(--v2-trust-body-size)', color: 'var(--v2-text-muted)',
          lineHeight: 1.6 }}>
          {message || 'This agent has not been scored yet. Scoring reads what discovery has '
            + 'observed — no runtime evidence is needed.'}
        </div>
      )}

      {state === 'ready' && passport && (
        <>
          <div style={{ display: 'flex', gap: 22, alignItems: 'center', flexWrap: 'wrap' }}>
            <Dial score={passport.score} style={style} />
            <div style={{ flex: 1, minWidth: 220 }}>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 9 }}>
                <Pill tone="warn">PRE-RUN · NOT YET MEASURED</Pill>
                {evidenceLabel(passport.rung) && <Pill>{evidenceLabel(passport.rung)}</Pill>}
                <Pill>CONFIDENCE {Number(passport.confidence).toFixed(2)}</Pill>
              </div>
              <p style={{ fontSize: 'var(--v2-trust-body-size)',
                color: 'var(--v2-text-secondary)', lineHeight: 1.6, margin: 0 }}>
                Describes what this agent is <em>able</em> to do — what it can reach, what protects
                it and what it is built from. It does not describe how it has behaved, because no
                runtime evidence has been recorded for it yet.
              </p>
            </div>
          </div>

          {deductions.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <SectionLabel>What lowered this score</SectionLabel>
              {deductions.map((d) => (
                <DeductionRow key={d.factorKey} name={factorName(d.factorKey)} points={d.points} />
              ))}
            </div>
          )}

          {Array.isArray(passport.factors) && passport.factors.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <SectionLabel>Factors</SectionLabel>
              {passport.factors.map((f) => <FactorRow key={f.factorKey} factor={f} nameOf={factorName} />)}
            </div>
          )}

          <p style={{ fontSize: 'var(--v2-trust-meta-size)', color: 'var(--v2-text-muted)',
            lineHeight: 1.6, marginTop: 14,
            paddingTop: 10, borderTop: '1px solid var(--v2-border-subtle)' }}>
            Scored on the published method, version {passport.methodologyVersion}. The score and its
            confidence are read together: confidence is how much of the defined input set was
            available, and a thin reading is not a clean bill of health.
          </p>
        </>
      )}
    </div>
  );
}

export default TrustScorePanel;
